import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminApiService, adminQueries } from '@/services/adminApi'

type QueryKey = readonly unknown[]

function useAdminMutation<TVariables>({
  mutationFn,
  invalidate,
  successMessage,
  errorMessage,
}: {
  mutationFn: (variables: TVariables) => Promise<unknown>
  invalidate?: (variables: TVariables) => QueryKey[]
  successMessage?: string
  errorMessage?: string
}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      invalidate?.(variables)?.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey })
      })

      if (successMessage) toast.success(successMessage)
    },
    onError: () => {
      if (errorMessage) toast.error(errorMessage)
    },
  })
}

export const useActivatePricingVersion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adminApiService.activatePricingVersion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.pricingList() })
      queryClient.invalidateQueries({ queryKey: adminQueries.pricingVersionsList() })
      queryClient.invalidateQueries({ queryKey: adminQueries.pricingActivationsList() })
      toast.success('Pricing version activated')
    },
    onError: () => {
      toast.error('Failed to activate pricing version')
    },
  })
}

export const useDeletePricingVersion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adminApiService.deletePricingVersion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.pricingVersionsList() })
      toast.success('Pricing version deleted')
    },
    onError: () => {
      toast.error('Failed to delete pricing version')
    },
  })
}

export const useTriggerPricingGeneration = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (autoActivate: boolean) => adminApiService.triggerPricingGeneration(autoActivate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.pricingGenerationStatus().queryKey })
      toast.success('Pricing generation started')
    },
    onError: () => {
      toast.error('Failed to start pricing generation')
    },
  })
}

export const useCancelPricingGeneration = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adminApiService.cancelPricingGeneration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.pricingGenerationStatus().queryKey })
      toast.success('Pricing generation cancelled')
    },
    onError: () => {
      toast.error('Failed to cancel pricing generation')
    },
  })
}

export const useSavePricingConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...config }: { id?: string } & Record<string, unknown>) =>
      id
        ? adminApiService.updatePricingConfig(id, config)
        : adminApiService.createPricingConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.pricingConfig().queryKey })
      toast.success('Pricing config saved')
    },
    onError: () => {
      toast.error('Failed to save pricing config')
    },
  })
}

export const useToggleProvider = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (provider: string) => adminApiService.toggleProvider(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.providers().queryKey })
    },
    onError: () => {
      toast.error('Failed to toggle provider')
    },
  })
}

export const useAdjustBalance = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId, amount, currency }: { accountId: string; amount: number; currency: string }) =>
      adminApiService.adjustAccountBalance(accountId, amount, currency),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueries.account(variables.accountId).queryKey })
      queryClient.invalidateQueries({ queryKey: adminQueries.accountsList() })
    },
  })
}

export const useUpdateRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId, role }: { accountId: string; role: string }) =>
      adminApiService.updateAccountRole(accountId, role),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueries.account(variables.accountId).queryKey })
      queryClient.invalidateQueries({ queryKey: adminQueries.accountsList() })
    },
  })
}

export const useDeleteAccount = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      adminApiService.deleteAccount(accountId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueries.account(variables.accountId).queryKey })
      queryClient.invalidateQueries({ queryKey: adminQueries.accountsList() })
    },
  })
}

export const useBlockAccount = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      adminApiService.blockAccount(accountId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueries.account(variables.accountId).queryKey })
      queryClient.invalidateQueries({ queryKey: adminQueries.accountsList() })
    },
  })
}

export const useUnblockAccount = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      adminApiService.unblockAccount(accountId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueries.account(variables.accountId).queryKey })
      queryClient.invalidateQueries({ queryKey: adminQueries.accountsList() })
    },
  })
}

export const useMigrateAccount = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId, targetAccountId }: { accountId: string; targetAccountId: string }) =>
      adminApiService.migrateAccount(accountId, targetAccountId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueries.account(variables.accountId).queryKey })
      queryClient.invalidateQueries({ queryKey: adminQueries.accountsList() })
    },
  })
}

export const useRegenerateReferralCode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      adminApiService.regenerateReferralCode(accountId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueries.account(variables.accountId).queryKey })
      queryClient.invalidateQueries({ queryKey: adminQueries.accountsList() })
    },
  })
}

export const useCreateReferralProgramme = () => {
  return useAdminMutation({
    mutationFn: (data: { rewards: { currency: string; referrerCreditCents?: number; refereeCreditCents?: number }[]; name?: string }) =>
      adminApiService.createReferralProgramme(data),
    invalidate: () => [adminQueries.referralProgrammesList()],
    successMessage: 'Referral programme created',
    errorMessage: 'Failed to create referral programme',
  })
}

export const useCreateAffiliate = () => {
  return useAdminMutation({
    mutationFn: ({ id, ...data }: { id: string; code: string; sharePercentage: number; revenueShareMonths: number; rewards: { currency: string; flatFeeCents?: number }[] }) =>
      adminApiService.createAffiliate(id, data),
    invalidate: () => [adminQueries.affiliatesList()],
    successMessage: 'Affiliate created',
    errorMessage: 'Failed to create affiliate',
  })
}

export const useUpdateAffiliate = () => {
  return useAdminMutation({
    mutationFn: ({ id, ...data }: { id: string; code: string; sharePercentage: number; revenueShareMonths: number; rewards: { currency: string; flatFeeCents?: number }[] }) =>
      adminApiService.updateAffiliate(id, data),
    invalidate: () => [adminQueries.affiliatesList()],
    successMessage: 'Affiliate updated',
    errorMessage: 'Failed to update affiliate',
  })
}

export const useCreateCampaign = () => {
  return useAdminMutation({
    mutationFn: (data: { code: string; name: string; description?: string; rewards: { currency: string; refereeCreditCents?: number | null }[]; maxUses?: number | null; activeFrom: string; activeUntil?: string | null }) =>
      adminApiService.createCampaign(data),
    invalidate: () => [adminQueries.campaignsList()],
    successMessage: 'Campaign created',
    errorMessage: 'Failed to create campaign',
  })
}

export const useDeleteCampaign = () => {
  return useAdminMutation({
    mutationFn: (id: string) => adminApiService.deleteCampaign(id),
    invalidate: () => [adminQueries.campaignsList()],
    successMessage: 'Campaign deactivated',
    errorMessage: 'Failed to deactivate campaign',
  })
}

export const useUpdateCampaign = () => {
  return useAdminMutation({
    mutationFn: ({ id, ...data }: { id: string; code: string; name: string; description?: string; rewards: { currency: string; refereeCreditCents?: number | null }[]; maxUses?: number | null; activeFrom: string; activeUntil?: string | null }) =>
      adminApiService.updateCampaign(id, data),
    invalidate: () => [adminQueries.campaignsList()],
    successMessage: 'Campaign updated',
    errorMessage: 'Failed to update campaign',
  })
}

export const useTriggerProviderCallSync = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ startDate, endDate, provider }: { startDate: string; endDate: string; provider?: string }) =>
      adminApiService.triggerProviderCallSync(startDate, endDate, provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.providerCallsList() })
      queryClient.invalidateQueries({ queryKey: adminQueries.providerCallSyncSummary().queryKey })
      toast.success('Sync started')
    },
    onError: () => {
      toast.error('Failed to start sync')
    },
  })
}

export const useResolveProviderCallFlag = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      adminApiService.resolveProviderCallFlag(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.providerCallsList() })
      queryClient.invalidateQueries({ queryKey: adminQueries.providerCallSyncSummary().queryKey })
      toast.success('Flag resolved')
    },
    onError: () => {
      toast.error('Failed to resolve flag')
    },
  })
}

export const useTriggerSchedulerTask = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (key: string) => adminApiService.triggerSchedulerTask(key),
    onSuccess: (_data, key) => {
      queryClient.invalidateQueries({ queryKey: adminQueries.schedulerTasks().queryKey })
      queryClient.invalidateQueries({ queryKey: adminQueries.schedulerRuns(key).queryKey })
      toast.success('Task triggered')
    },
    onError: () => {
      toast.error('Failed to trigger task')
    },
  })
}

export const useUpdateSchedulerTask = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      adminApiService.updateSchedulerTask(key, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.schedulerTasks().queryKey })
    },
    onError: () => {
      toast.error('Failed to update task')
    },
  })
}

export const useInitiateAgentCall = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { to: string; voice?: string; language?: string; initialSayText?: string }) =>
      adminApiService.initiateAgentCall(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.agentCallsList() })
    },
    onError: () => {
      toast.error('Failed to initiate agent call')
    },
  })
}

export const useInitiateBrowserCall = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { voice?: string; language?: string; initialSayText?: string }) =>
      adminApiService.initiateBrowserCall(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.agentCallsList() })
    },
    onError: () => {
      toast.error('Failed to initiate browser call')
    },
  })
}

export const useAgentCallSay = () => {
  return useMutation({
    mutationFn: ({ id, text, voice }: { id: string; text: string; voice?: string }) =>
      adminApiService.agentCallSay(id, text, voice),
    onError: () => {
      toast.error('Failed to send message')
    },
  })
}

export const useAgentCallDtmf = () => {
  return useMutation({
    mutationFn: ({ id, digits }: { id: string; digits: string }) =>
      adminApiService.agentCallDtmf(id, digits),
    onError: () => {
      toast.error('Failed to send DTMF')
    },
  })
}

export const useAgentCallHangup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adminApiService.agentCallHangup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueries.agentCallsList() })
    },
    onError: () => {
      toast.error('Failed to hang up call')
    },
  })
}

export const useSyncCall = () => {
  return useAdminMutation({
    mutationFn: (id: number) => adminApiService.syncCall(id),
    invalidate: (_id) => [adminQueries.calls(1, 20)()],
    successMessage: 'Call synced successfully',
    errorMessage: 'Failed to sync call',
  })
}
